@if (form) {
  <form class="startauditautomation-container form" [formGroup]="form">
    <div class="form-section">
      <div class="form-section-info">
        <h4>Select Activities</h4>
        <div class="description">Choose the audit activities to be executed in this automation.</div>
      </div>
      <div class="form-section-content flex">
        <div class="form-item custom-element">
          <label>Automate entire audit</label>
          <p-toggle-switch formControlName="fullAudit"></p-toggle-switch>
          @if (!loadingAuditInfo && (testObjects || []).length == 0) {
            <div class="warning-label">
              <fa-icon [icon]="faExclamationCircle"></fa-icon>
              No test object has been added to the audit yet
            </div>
          }
          @if (!loadingAuditInfo && form.get('fullAudit').value && testObjectsWithoutActivity.length >= 1) {
            <div class="warning-label">
              <fa-icon [icon]="faExclamationCircle"></fa-icon>
              No activity planned for test object{{ testObjectsWithoutActivity.length > 1 ? 's' : '' }}:
              <ng-container *ngFor="let name of testObjectsWithoutActivity; let last = last">
                {{ name }}<span *ngIf="!last">, </span>
              </ng-container>
            </div>
          }
        </div>
        @if (!form.get('fullAudit').value) {
          <div class="form-item">
            <h4 class="form-label">Select Activities</h4>
            <div class="form-item-content select-activities">
              <p-button label="Choose from plan" (click)="openPlanningForSelection()"></p-button>
              <div class="selection-info">
                <div class="selected-activities">
                  {{ (form.get('activitiesToAutomate').value || []).length }} automatable activities selected
                </div>
                @if (plannedCustomObjects.length > 0) {
                  <div class="warning-label">
                    <fa-icon [icon]="faExclamationCircle"></fa-icon>
                    {{ plannedCustomObjects.length }} of the activities you selected can't be automated
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
    <div class="form-section">
      <div class="form-section-info">
        <h4>Automation Results</h4>
        <div class="description">How to handle the results from the automation run.</div>
      </div>
      <div class="form-section-content flex">
        <div class="form-item custom-element">
          <p-check-box formControlName="associateFindingsWithAudit" [binary]="true" data-testing-id="associate-findings-with-audit-checkbox"></p-check-box>
          <label>Associate findings automatically with audit.</label>
        </div>
        <div class="form-item custom-element">
          <p-check-box formControlName="autoSetEvaluationStatusForAssessmentTasks" [binary]="true"></p-check-box>
          <label>Set an automatic evaluation status for activities (e.g., mark them ‘Failed’ or ‘Passed‘).</label>
        </div>
        <div class="form-item custom-element">
          <p-check-box formControlName="setExecutionStatusForAutomatedActivities" [binary]="true"></p-check-box>
          <label>Set the execution status of automated activities to:</label>
          <p-select formControlName="executionStatusForAutomatedActivities" [options]="executionStatusOptions"></p-select>
        </div>
        <div class="form-item custom-element">
          <p-check-box formControlName="setLabelsForAutomatedActivities" [binary]="true"></p-check-box>
          <label>Set labels for automated activities to:</label>
          <div class="tags-container">
            <wm-tags [onlyLocal]="true" [tags]="form.get('labelsForAutomatedActivities').value || []" (tagsChanged)="setLabelsForAutomatedActivities($event)"></wm-tags>
          </div>
        </div>
        <div class="form-item custom-element">
          <p-check-box formControlName="setLabelsForAutomatedFindings" [binary]="true"></p-check-box>
          <label>Set labels for automated findings to:</label>
          <div class="tags-container">
            <wm-tags [onlyLocal]="true" [tags]="form.get('labelsForAutomatedFindings').value || []" (tagsChanged)="setLabelsForAutomatedFindings($event)"></wm-tags>
          </div>
        </div>
        <div class="form-item custom-element">
          <p-check-box formControlName="enableAiAnalysis" [binary]="true" data-testing-id="use-ai-checkbox"></p-check-box>
          <label>Use AI during automation.</label>
        </div>
        @if (hasWebTestObjects) {
          <div class="form-item analysis-language-item">
            <div class="analysis-language-control">
              <label>Language for web analysis</label>
              <p-dropdown formControlName="contentAnalysisLanguage"
                          [options]="contentAnalysisLanguageOptions"
                          optionLabel="label"
                          optionValue="value">
              </p-dropdown>
            </div>
            @if (hasMobileTestObjects) {
              <div class="analysis-language-description">
                Applies only to Web Test Objects. Mobile App Test Objects are unaffected.
              </div>
            }
          </div>
        }
      </div>

    </div>

    @if (hasWebTestObjects || hasMobileTestObjects) {
      <div class="form-section">
        <div class="form-section-info">
          <h4>Automation Policies</h4>
          <div class="description">
            Configure actions performed before Test Objects are analyzed.
          </div>
        </div>
        <div class="form-section-content flex automation-policy-sections">
          @if (hasWebTestObjects) {
            <div class="form-item">
              <h4 class="form-label">Web Test Objects</h4>
              <div class="description">Actions applied to browser-based Test Objects during automation.</div>
              <wm-automation-policy-editor mode="web" formControlName="webAutomationPolicy"></wm-automation-policy-editor>
            </div>
          }
          @if (hasMobileTestObjects) {
            <div class="form-item">
              <h4 class="form-label">Mobile App Test Objects</h4>
              <div class="description">Actions applied to native mobile apps after launch.</div>
              <wm-automation-policy-editor mode="mobile" formControlName="mobileAutomationPolicy"></wm-automation-policy-editor>
            </div>
          }
        </div>
      </div>
    }
    <div class="form-section">
      <div class="form-section-info">
        <h4>Report Generation</h4>
        <div class="description">Configure if a report is generated after automation.</div>
      </div>
      <div class="form-section-content flex">
        <div class="form-item custom-element">
          <label>Generate report</label>
          <p-toggle-switch formControlName="generateReport"></p-toggle-switch>
        </div>
        @if (form.get('generateReport').value) {
          <div class="form-item">
            <h4 class="form-label">Select Report Template</h4>
            <div class="form-item-content select-activities">
              <p-button label="Configure Report" (click)="configureReport()"></p-button>
              <div class="selected-template">
                Selected template:
                <div class="template">
                  @if (reportTemplate) {
                    <div class="template-label">{{ reportTemplate.name }}</div>
                    <div class="template-description">{{ reportTemplate.description }}</div>
                  } @else {
                    <div class="template-label">No template selected</div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
    <div class="form-section last-section">
      <div class="form-section-info">
      </div>
      <div class="form-section-content flex">
        <div class="wm-l-startaudit-controls">
          <p-button size="large" data-testing-id="start-automation-confirm-btn" (click)="startAutomation()">@if (starting) {
            <fa-icon [icon]="faSyncAlt" animation="spin"></fa-icon>
          } Start Automation
          </p-button>
        </div>
      </div>
    </div>
  </form>
} @else {
  <div class="wm-l-startaudit-container-loading">
    <div>
      <p-progressSpinner></p-progressSpinner>
    </div>
    <div>Loading...</div>
  </div>
}
